// =============================================================================
// BP-08-05: Vendor Portal Frontend - useVendorReviews Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorReviewsApi } from '@/features/vendor/api/vendorApi';
import type { VendorReview, ReviewListParams } from '@/features/vendor/types/vendorTypes';

// Query keys
export const vendorReviewKeys = {
  all: ['vendor-reviews'] as const,
  lists: () => [...vendorReviewKeys.all, 'list'] as const,
  list: (params: ReviewListParams) => [...vendorReviewKeys.lists(), params] as const,
  pending: () => [...vendorReviewKeys.all, 'pending'] as const,
};

/**
 * Hook for fetching vendor reviews list
 */
export function useVendorReviews(params: ReviewListParams = {}) {
  return useQuery({
    queryKey: vendorReviewKeys.list(params),
    queryFn: () => vendorReviewsApi.list(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for fetching reviews that need a response
 */
export function useVendorPendingReviews() {
  return useQuery({
    queryKey: vendorReviewKeys.pending(),
    queryFn: () => vendorReviewsApi.list({ hasResponse: false, pageSize: 5 }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for responding to a review
 */
export function useRespondToReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, response }: { reviewId: string; response: string }) =>
      vendorReviewsApi.respond(reviewId, response),
    onSuccess: () => {
      // Invalidate all review lists to refresh
      queryClient.invalidateQueries({ queryKey: vendorReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorReviewKeys.pending() });
    },
  });
}

/**
 * Hook for updating a review response
 */
export function useUpdateReviewResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, response }: { reviewId: string; response: string }) =>
      vendorReviewsApi.updateResponse(reviewId, response),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorReviewKeys.lists() });
    },
  });
}

/**
 * Hook for deleting a review response
 */
export function useDeleteReviewResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reviewId: string) => vendorReviewsApi.deleteResponse(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorReviewKeys.pending() });
    },
  });
}

/**
 * Hook for review statistics
 */
export function useReviewStats(params: ReviewListParams = {}) {
  const { data, ...rest } = useVendorReviews(params);

  return {
    ...rest,
    stats: data
      ? {
          total: data.total,
          averageRating: data.averageRating,
          distribution: data.ratingDistribution,
          pendingResponses: data.items.filter((r) => !r.vendorResponse).length,
        }
      : null,
  };
}

/**
 * Helper to format rating distribution for charts
 */
export function formatRatingDistribution(distribution: Record<number, number>) {
  return [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: distribution[rating] || 0,
    label: `${rating} stars`,
  }));
}

/**
 * Helper to calculate response rate
 */
export function calculateResponseRate(reviews: VendorReview[]): number {
  if (reviews.length === 0) return 0;
  const responded = reviews.filter((r) => r.vendorResponse).length;
  return Math.round((responded / reviews.length) * 100);
}

